import {useChangeLanguageMutation, useMeQuery} from '@progyny/generated/graphql'
import {useHeap} from '@progyny/hooks/useHeap'
import {useLanguageCriteria} from '@progyny/store/languageStore'
import {useRefetchContext} from '@progyny/store/refetchStore'
import {updateDocumentDirection} from '@progyny/utils/rtl'

import {updateTranslations} from '@root/i18n'

const useChangeLanguage = () => {
  const {data: meQuery, refetch} = useMeQuery()
  const {triggerAllRefetch} = useRefetchContext()
  const {trackHeapEvent} = useHeap()
  const {setDefaultLanguage, setIsLanguageUpdated} = useLanguageCriteria()
  const [changeLanguageMutation] = useChangeLanguageMutation()

  const updateLanguage = async (newLanguage: string) => {
    try {
      const res = await changeLanguageMutation({
        variables: {language: newLanguage},
      })

      if (res.data) {
        setDefaultLanguage(newLanguage)
        updateHtmlLang(newLanguage)
        updateDocumentDirection(newLanguage)
        trackHeapEvent('pgChangeLanguage', {
          pgOriginalLanguage: meQuery?.me?.member?.language,
          pgSelectedLanguage: newLanguage,
        })
        triggerAllRefetch()
        refetch()
        setIsLanguageUpdated(true)
      }
    } catch (error) {
      console.error('Error updating language:', error)
    }
  }

  const changeLanguage = async (
    newLanguage: string,
    noAuth?: boolean
  ): Promise<void> => {
    const previousLanguage = localStorage.getItem('ui_language')

    // Set storage first so routes resync effect (which can run when refetch completes mid-flow) reads the new value and does not overwrite with changeLanguage(previousLanguage).
    localStorage.setItem('ui_language', newLanguage)

    // Fetch translations; then switch UI. Throw so callers can keep dropdown state on failure.
    const success = await updateTranslations(newLanguage, false)
    if (!success) {
      localStorage.setItem('ui_language', previousLanguage || '')
      throw new Error(`Failed to load translations for ${newLanguage}`)
    }

    updateDocumentDirection(newLanguage)
    updateHtmlLang(newLanguage)
    setDefaultLanguage(newLanguage)

    if (!noAuth) {
      await updateLanguage(newLanguage)
    }
  }

  const updateHtmlLang = (language?: string) =>
    language && document.documentElement.setAttribute('lang', language)

  return {changeLanguage, updateHtmlLang}
}

export default useChangeLanguage
